/**
 * Shared UI sizes/styles for add/edit form modals & dialogs.
 * Same outer shell (width/padding/top) for every form; densify fields first,
 * then scroll inside the body only when content still exceeds the viewport.
 */

/** Standard viewport inset from top. */
export const STANDARD_FORM_MODAL_TOP_PX = 120;
export const STANDARD_FORM_MODAL_TOP = `${STANDARD_FORM_MODAL_TOP_PX}px`;
/** Small bottom inset so forms keep usable height under the top margin. */
export const STANDARD_FORM_MODAL_BOTTOM_PX = 24;
/** Available height below the top inset. */
export const STANDARD_FORM_MODAL_MAX_HEIGHT = `calc(100vh - ${STANDARD_FORM_MODAL_TOP_PX + STANDARD_FORM_MODAL_BOTTOM_PX}px)`;
export const STANDARD_FORM_DIALOG_MARGIN = `${STANDARD_FORM_MODAL_TOP} auto ${STANDARD_FORM_MODAL_BOTTOM_PX}px`;

/** Shared outer padding (title / body / footer whitespace). */
export const STANDARD_FORM_SHELL_PADDING_SX = {
  p: 1.25,
  pt: 0.5,
  borderRadius: "8px",
  boxSizing: "border-box",
};

/** Fixed top anchor — modals start at the same Y regardless of content height. */
export const STANDARD_FORM_MODAL_POSITION_SX = {
  position: "absolute",
  top: STANDARD_FORM_MODAL_TOP,
  left: "50%",
  transform: "translateX(-50%)",
  margin: 0,
};

/** Standard form window — same width/height band for all BasicModal forms. */
export const STANDARD_FORM_MODAL_SX = {
  ...STANDARD_FORM_MODAL_POSITION_SX,
  ...STANDARD_FORM_SHELL_PADDING_SX,
  width: "min(900px, 96vw)",
  maxWidth: "96vw",
  maxHeight: STANDARD_FORM_MODAL_MAX_HEIGHT,
  overflowX: "hidden",
  overflowY: "auto",
};

/** Alias — same shell as STANDARD (kept for existing imports). */
export const NO_INNER_SCROLL_FORM_MODAL_SX = { ...STANDARD_FORM_MODAL_SX };

/** Create/edit invoice — same shell dimensions as other forms. */
export const INVOICE_FORM_MODAL_SX = { ...STANDARD_FORM_MODAL_SX };

/** @deprecated Use STANDARD_FORM_MODAL_SX — kept as aliases for existing imports. */
export const LARGE_FORM_MODAL_SX = { ...STANDARD_FORM_MODAL_SX };
export const MEDIUM_FORM_MODAL_SX = { ...STANDARD_FORM_MODAL_SX };
export const COMPACT_FORM_MODAL_SX = { ...STANDARD_FORM_MODAL_SX };

/**
 * Full-page form paper (hospital onboarding) — medium card width like the reference layout.
 */
export const HOSPITAL_FORM_MAX_WIDTH = "800px";

export const FORM_PAGE_PAPER_SX = {
  ...STANDARD_FORM_SHELL_PADDING_SX,
  width: "100%",
  maxWidth: HOSPITAL_FORM_MAX_WIDTH,
  mx: "auto",
  overflow: "visible",
  boxShadow: 1,
};

/** MUI Dialog props matching shared form window. */
export const STANDARD_FORM_DIALOG_PROPS = {
  maxWidth: "md",
  fullWidth: true,
  scroll: "paper",
};

export const STANDARD_FORM_DIALOG_PAPER_SX = {
  "& .MuiDialog-container": {
    alignItems: "flex-start",
  },
  "& .MuiDialog-paper": {
    margin: STANDARD_FORM_DIALOG_MARGIN,
    padding: "10px 14px",
    borderRadius: "8px",
    maxHeight: STANDARD_FORM_MODAL_MAX_HEIGHT,
    width: "min(900px, 96vw)",
    maxWidth: "96vw",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  "& .MuiDialogTitle-root": {
    flexShrink: 0,
    pt: 0.5,
    pb: 1,
  },
  "& .MuiDialogContent-root": {
    flex: "1 1 auto",
    minHeight: 0,
    overflowX: "hidden",
    overflowY: "auto",
    paddingTop: "6px !important",
    paddingBottom: "6px !important",
  },
  "& .MuiDialogActions-root": {
    flexShrink: 0,
    width: "100%",
    boxSizing: "border-box",
  },
};

/** Fit-to-content dialogs — no shell scrollbar (Edit Hospital, Cash Payment). */
export const FIT_CONTENT_DIALOG_PAPER_SX = {
  ...STANDARD_FORM_DIALOG_PAPER_SX,
  "& .MuiDialog-paper": {
    ...STANDARD_FORM_DIALOG_PAPER_SX["& .MuiDialog-paper"],
    maxHeight: "none !important",
    height: "auto",
    overflow: "visible !important",
  },
  "& .MuiDialogContent-root": {
    flex: "0 0 auto !important",
    minHeight: "auto !important",
    maxHeight: "none !important",
    overflowX: "hidden !important",
    overflowY: "visible !important",
    paddingTop: "6px !important",
    paddingBottom: "6px !important",
  },
};

/** Edit Hospital dialog — same medium width as onboarding card; no scrollbar. */
export const HOSPITAL_FORM_DIALOG_PAPER_SX = {
  ...FIT_CONTENT_DIALOG_PAPER_SX,
  "& .MuiDialog-paper": {
    ...FIT_CONTENT_DIALOG_PAPER_SX["& .MuiDialog-paper"],
    width: `min(${HOSPITAL_FORM_MAX_WIDTH}, 96vw)`,
    maxWidth: "96vw",
  },
};

/** Cash Payment Details (onboarding / org utilities) — no scrollbar. */
export const CASH_PAYMENT_DIALOG_PAPER_SX = {
  ...FIT_CONTENT_DIALOG_PAPER_SX,
  "& .MuiDialog-paper": {
    ...FIT_CONTENT_DIALOG_PAPER_SX["& .MuiDialog-paper"],
    width: `min(${HOSPITAL_FORM_MAX_WIDTH}, 96vw)`,
    maxWidth: "96vw",
  },
};

/** OCR upload dialogs — same shell; slightly narrower content width. */
export const OCR_UPLOAD_DIALOG_PAPER_SX = {
  ...STANDARD_FORM_DIALOG_PAPER_SX,
  "& .MuiDialog-paper": {
    ...STANDARD_FORM_DIALOG_PAPER_SX["& .MuiDialog-paper"],
    width: "min(720px, 96vw)",
  },
  "& .MuiDialogContent-root": {
    ...STANDARD_FORM_DIALOG_PAPER_SX["& .MuiDialogContent-root"],
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
};

/** Raw MUI Modal surface (non-BasicModal) — same top anchor + shell. */
export const STANDARD_MODAL_SURFACE_SX = {
  ...STANDARD_FORM_MODAL_POSITION_SX,
  ...STANDARD_FORM_SHELL_PADDING_SX,
  bgcolor: "background.paper",
  boxShadow: 24,
  width: "min(900px, 96vw)",
  maxWidth: "96vw",
  maxHeight: STANDARD_FORM_MODAL_MAX_HEIGHT,
  overflowX: "hidden",
  overflowY: "auto",
};

export const STANDARD_FORM_DIALOG_TITLE_SX = {
  fontSize: "1.5rem",
  fontWeight: "bold",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  pt: 0,
  pb: 1,
  mb: 0,
  borderBottom: "1px solid",
  borderColor: "divider",
};

/** Shared light separator — same weight/color for header & footer lines. */
export const FORM_DIVIDER_SX = {
  borderColor: "divider",
  opacity: 1,
};

/** Modal header block: title + divider, minimal top gap */
export const FORM_MODAL_HEADER_SX = {
  pt: 0,
  pb: 0.5,
  mb: 0.25,
  mt: 0,
  px: 0.5,
};

export const FORM_MODAL_TITLE_SX = {
  fontSize: "1.5rem",
  fontWeight: "bold",
  textAlign: "center",
  width: "100%",
  pt: 0,
  pb: 0.5,
  m: 0,
  lineHeight: 1.25,
};

/** In-page form section (onboarding steps) */
export const FORM_SECTION_HEADER_SX = {
  mb: 0.75,
  mt: 0.25,
};

export const FORM_SECTION_TITLE_SX = {
  fontSize: "0.9rem",
  fontWeight: 700,
  color: "text.primary",
  pb: 0.4,
  m: 0,
};

/** Light summary box used in create-invoice confirm step */
export const FORM_CONFIRM_DETAILS_BOX_SX = {
  my: 1,
  p: 1.25,
  borderRadius: 1,
  bgcolor: "#f8fafc",
  border: "1px solid",
  borderColor: "divider",
};

/** Onboarding review step — compact */
export const ONBOARDING_REVIEW_CONTENT_SX = {
  mt: 0.5,
  overflow: "visible",
  maxHeight: "none",
};

/** Compact success step modal (patient + invoice created). */
export const SUCCESS_STEP_MODAL_SX = {
  ...STANDARD_FORM_MODAL_SX,
  width: "min(520px, 96vw)",
  maxWidth: "520px",
};

export const STANDARD_FORM_DIALOG_ACTIONS_SX = {
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  px: 0.5,
  pb: 0.25,
  pt: 0.75,
  borderTop: "1px solid",
  borderColor: "divider",
};

/** Footer block above action buttons (divider + spacing, mirrors header). */
export const FORM_MODAL_FOOTER_SX = {
  width: "100%",
  mt: 0.25,
  mb: 0,
  pt: 0,
};

export const FORM_MODAL_FOOTER_ACTIONS_SX = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  pt: 0.75,
  pb: 0.25,
  px: 0.5,
};

/** Centered modal heading (BasicModal internal titles). */
export const STANDARD_FORM_HEADING_SX = {
  ...FORM_MODAL_TITLE_SX,
};

/**
 * Cancel: transparent/white bg, blue text (outlined primary).
 * Submit: primary blue contained.
 */
export const FORM_CANCEL_BUTTON_SX = {
  bgcolor: "transparent",
  color: "primary.main",
  borderColor: "primary.main",
  minWidth: 96,
  fontSize: "0.8125rem",
  fontWeight: 600,
  textTransform: "none",
  py: 0.35,
  px: 1.25,
  "&:hover": {
    bgcolor: "rgba(86, 138, 247, 0.08)",
    borderColor: "primary.main",
  },
};

export const FORM_SUBMIT_BUTTON_SX = {
  minWidth: 96,
  fontSize: "0.8125rem",
  fontWeight: 600,
  textTransform: "none",
  py: 0.35,
  px: 1.25,
};

/** Dense TextField defaults for tall forms (UI only). */
export const DENSE_FIELD_PROPS = {
  size: "small",
  margin: "dense",
};

/** Content wrapper: avoid nested scrollbars inside BasicModal. */
export const FORM_MODAL_CONTENT_SX = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxHeight: "none",
  overflow: "visible",
  p: 0,
};

/** Dense form body styles (apply on wrapper around form fields). */
export const DENSE_FORM_BODY_SX = {
  width: "100%",
  "& .MuiTextField-root": { my: 0 },
  "& .MuiFormControl-root": { my: 0 },
  "& .MuiFormHelperText-root": {
    mt: 0.05,
    mb: 0,
    mx: 0,
    lineHeight: 1.05,
    fontSize: "0.65rem",
    minHeight: "0.9em",
  },
  "& .MuiInputLabel-root": { lineHeight: 1.1, fontSize: "0.8rem" },
  "& .MuiInputLabel-shrink": { fontSize: "0.72rem" },
  "& .MuiOutlinedInput-root": { minHeight: 32 },
  "& .MuiOutlinedInput-input": { py: "4px", fontSize: "0.8125rem" },
  "& .MuiSelect-select": { py: "4px !important", fontSize: "0.8125rem" },
  "& .MuiAutocomplete-inputRoot": { py: "0px !important", minHeight: 32 },
  "& .MuiFormControlLabel-root": { mr: 1, my: 0 },
  "& .MuiCheckbox-root": { p: 0.35 },
  "& h3": { mt: "2px !important", mb: "2px !important", fontSize: "0.85rem" },
  "& .MuiButton-sizeLarge": { py: 0.6 },
  "& .MuiDivider-root": { my: "4px !important" },
  "& .MuiTypography-h5": { fontSize: "1.5rem", fontWeight: "bold", pb: "4px !important", textAlign: "center" },
  "& .MuiTypography-h4": { fontSize: "1.5rem", fontWeight: "bold", pb: "4px !important", textAlign: "center" },
  "& .MuiTypography-subtitle1": { fontSize: "0.85rem", lineHeight: 1.15 },
  "& .MuiGrid-container": { rowGap: "2px" },
};

/** DialogContent sx for fit-to-content dialogs (overrides theme overflowY: auto). */
export const FIT_CONTENT_DIALOG_CONTENT_SX = {
  ...DENSE_FORM_BODY_SX,
  flex: "0 0 auto",
  minHeight: "auto",
  maxHeight: "none",
  overflowX: "hidden",
  overflowY: "visible",
};

/** Override global form-sized MuiDialog defaults for mobile clock/time pickers. */
export const COMPACT_MOBILE_TIME_PICKER_SLOT_PROPS = {
  dialog: {
    fullWidth: false,
    maxWidth: false,
    sx: {
      "& .MuiDialog-paper": {
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        minWidth: 280,
        margin: "16px auto !important",
        maxHeight: "none",
        padding: "0 !important",
        overflow: "visible",
        display: "block",
        flexDirection: "unset",
      },
      "& .MuiDialogContent-root": {
        flex: "0 0 auto",
        minHeight: "auto",
        maxHeight: "none",
        overflow: "visible",
        padding: "0 !important",
      },
      "& .MuiDialogTitle-root": {
        display: "none",
      },
      "& .MuiDialogActions-root": {
        borderTop: "none",
        paddingTop: 0,
      },
    },
  },
};

export default STANDARD_FORM_MODAL_SX;
